import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/**
 * Navbar entry: search icon only — opens the dedicated Search page.
 * ⌘K / Ctrl+K jumps to /search when focus is not in an editable field (when enableShortcut is true).
 */
const GlobalSearch = ({ className = '', enableShortcut = true }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isEVFlow =
    location.pathname === '/ev-dashboard' ||
    location.pathname === '/ev_crisis_simulator' ||
    (location.pathname === '/search' &&
      new URLSearchParams(location.search).get('context') === 'ev');
  const searchTarget = isEVFlow ? '/search?context=ev' : '/search';

  useEffect(() => {
    if (!enableShortcut) return undefined;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) {
        return;
      }
      e.preventDefault();
      navigate(searchTarget);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, enableShortcut, searchTarget]);

  return (
    <Link
      to={searchTarget}
      className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
        isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      } ${className}`.trim()}
      aria-label="Search"
      title="Search"
    >
      <Search className="h-5 w-5" strokeWidth={2} />
    </Link>
  );
};

export default GlobalSearch;
